import React, { useState, useEffect } from "react";
import axios from "axios";
import { ACCESS_KEY } from "../config";
import "./AgencyRecharge.css";

const AgencyRecharge = () => {
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showLessModal, setShowLessModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [addCoinAmount, setAddCoinAmount] = useState("");
  const [lessCoinAmount, setLessCoinAmount] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const LIMIT = 20;
  const API_BASE = "https://admin.butterlive.co.in";

  useEffect(() => {
    fetchAgencies();
  }, [page, search]);

  const fetchAgencies = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_BASE}/management/agencyCoinSystem/agencies`,
        {
          params: {
            start: page,
            limit: LIMIT,
            search: search.trim(),
          },
          headers: {
            key: ACCESS_KEY,
          },
        }
      );

      if (response.data?.status) {
        setAgencies(response.data.data || []);
        setTotal(Number(response.data.total || 0));
      } else {
        setAgencies([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Agency Recharge - fetch agencies error:", error);
      setAgencies([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.max(Math.ceil(total / LIMIT), 1);

  const openAddModal = (agency) => {
    setSelectedAgency(agency);
    setAddCoinAmount("");
    setShowAddModal(true);
  };

  const openLessModal = (agency) => {
    setSelectedAgency(agency);
    setLessCoinAmount("");
    setShowLessModal(true);
  };

  const handleAddCoin = async () => {
    const amount = Number(addCoinAmount);

    if (!Number.isInteger(amount) || amount <= 0) {
      alert("Please enter a valid positive whole number.");
      return;
    }

    if (!selectedAgency?._id) {
      alert("Agency not selected.");
      return;
    }

    try {
      setActionLoading(true);

      const response = await axios.post(
        `${API_BASE}/management/agencyCoinSystem/add`,
        {
          agencyId: selectedAgency._id,
          coin: amount,
        },
        {
          headers: {
            key: ACCESS_KEY,
          },
        }
      );

      if (!response.data?.status) {
        alert(response.data?.message || "Failed to add rechargeable coins.");
        return;
      }

      alert("Rechargeable coins added successfully.");

      closeModals();
      await fetchAgencies();
    } catch (error) {
      console.error("Agency Recharge - add coin error:", error);

      alert(
        error.response?.data?.message ||
          "Something went wrong while adding rechargeable coins."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleLessCoin = async () => {
    const amount = Number(lessCoinAmount);
    const currentBalance = Number(selectedAgency?.rechargeableCoin || 0);

    if (!Number.isInteger(amount) || amount <= 0) {
      alert("Please enter a valid positive whole number.");
      return;
    }

    if (!selectedAgency?._id) {
      alert("Agency not selected.");
      return;
    }

    if (amount > currentBalance) {
      alert(`Insufficient balance. Current balance: ${currentBalance.toLocaleString()}`);
      return;
    }

    try {
      setActionLoading(true);

      const response = await axios.post(
        `${API_BASE}/management/agencyCoinSystem/less`,
        {
          agencyId: selectedAgency._id,
          coin: amount,
        },
        {
          headers: {
            key: ACCESS_KEY,
          },
        }
      );

      if (!response.data?.status) {
        alert(response.data?.message || "Failed to deduct rechargeable coins.");
        return;
      }

      alert("Rechargeable coins deducted successfully.");

      closeModals();
      await fetchAgencies();
    } catch (error) {
      console.error("Agency Recharge - less coin error:", error);

      alert(
        error.response?.data?.message ||
          "Something went wrong while deducting rechargeable coins."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openHistoryModal = async (agency) => {
    setSelectedAgency(agency);
    setShowHistoryModal(true);
    setHistoryPage(1);
    await fetchAgencyHistory(agency._id, 1);
  };

  const fetchAgencyHistory = async (agencyId, pageNumber = 1) => {
    if (!agencyId) return;

    try {
      setHistoryLoading(true);

      const response = await axios.get(
        `${API_BASE}/management/agencyCoinSystem/history`,
        {
          params: {
            agencyId,
            start: pageNumber,
            limit: 10,
          },
          headers: {
            key: ACCESS_KEY,
          },
        }
      );

      if (response.data?.status) {
        setHistory(response.data.data || []);
        setHistoryTotal(Number(response.data.total || 0));
      } else {
        setHistory([]);
        setHistoryTotal(0);
      }
    } catch (error) {
      console.error("Agency Recharge - history error:", error);
      setHistory([]);
      setHistoryTotal(0);
      alert(
        error.response?.data?.message ||
          "Something went wrong while loading history."
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const changeHistoryPage = async (newPage) => {
    if (!selectedAgency?._id) return;

    const totalHistoryPages = Math.max(
      Math.ceil(historyTotal / 10),
      1
    );

    if (newPage < 1 || newPage > totalHistoryPages) return;

    setHistoryPage(newPage);
    await fetchAgencyHistory(selectedAgency._id, newPage);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowLessModal(false);
    setShowHistoryModal(false);
    setSelectedAgency(null);
    setHistory([]);
    setHistoryPage(1);
    setHistoryTotal(0);
  };

  return (
    <div className="agency-recharge-page">
      <div className="page-header">
        <h1 className="page-title">Agency Recharge</h1>
      </div>

      <div className="agency-recharge-toolbar">
        <input
          type="text"
          className="agency-recharge-search"
          placeholder="Search Agency ID or Name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="agency-recharge-table-card">
        <div className="agency-recharge-table-wrap">
          <table className="agency-recharge-table">
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Photo</th>
                <th>Agency Name</th>
                <th>Unique ID</th>
                <th>Agency Code</th>
                <th>Rechargeable Coin</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="agency-recharge-no-data">
                    Loading Agencies...
                  </td>
                </tr>
              ) : agencies.length === 0 ? (
                <tr>
                  <td colSpan="7" className="agency-recharge-no-data">
                    No Agency Found
                  </td>
                </tr>
              ) : (
                agencies.map((agency, index) => (
                  <tr key={agency._id}>
                    <td>{index + 1}</td>

                    <td>
                      {agency.image ? (
                        <img
                          src={agency.image}
                          alt=""
                          className="agency-recharge-avatar"
                        />
                      ) : (
                        <div className="agency-recharge-avatar-placeholder">
                          {agency.name?.charAt(0)?.toUpperCase() || "A"}
                        </div>
                      )}
                    </td>

                    <td className="agency-recharge-name">
                      {agency.name}
                    </td>

                    <td>{agency.uniqueId}</td>

                    <td className="agency-recharge-code">
                      {agency.agencyCode}
                    </td>

                    <td className="agency-recharge-balance">
                      {Number(agency.rechargeableCoin || 0).toLocaleString()}
                    </td>

                    <td>
                      <div className="agency-recharge-actions">
                        <button
                          type="button"
                          className="agency-action-btn add"
                          onClick={() => openAddModal(agency)}
                        >
                          + Add
                        </button>

                        <button
                          type="button"
                          className="agency-action-btn less"
                          onClick={() => openLessModal(agency)}
                        >
                          − Less
                        </button>

                        <button
                          type="button"
                          className="agency-action-btn history"
                          onClick={() => openHistoryModal(agency)}
                        >
                          History
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="agency-recharge-pagination">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          >
            Previous
          </button>

          <button type="button" className="active">
            {page}
          </button>

          <button
            type="button"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* ADD COIN MODAL */}
      {showAddModal && selectedAgency && (
        <div className="agency-recharge-modal-overlay" onClick={closeModals}>
          <div
            className="agency-recharge-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="agency-recharge-modal-header">
              <h2>Add Recharge Coin</h2>

              <button type="button" onClick={closeModals}>
                ×
              </button>
            </div>

            <div className="agency-recharge-modal-body">
              <div className="agency-recharge-info">
                <span>Agency</span>
                <strong>{selectedAgency.name}</strong>
              </div>

              <div className="agency-recharge-info">
                <span>Unique ID</span>
                <strong>{selectedAgency.uniqueId}</strong>
              </div>

              <div className="agency-recharge-info">
                <span>Current Balance</span>
                <strong>
                  {Number(
                    selectedAgency.rechargeableCoin || 0
                  ).toLocaleString()}
                </strong>
              </div>

              <label>Add Coins</label>

              <input
                type="number"
                min="1"
                step="1"
                placeholder="Enter coins"
                className="agency-recharge-modal-input"
                value={addCoinAmount}
                onChange={(e) => setAddCoinAmount(e.target.value)}
                disabled={actionLoading}
              />
            </div>

            <div className="agency-recharge-modal-footer">
              <button
                type="button"
                className="modal-cancel-btn"
                onClick={closeModals}
              >
                Cancel
              </button>

              <button
                type="button"
                className="modal-confirm-btn add"
                onClick={handleAddCoin}
                disabled={actionLoading}
              >
                {actionLoading ? "Adding..." : "Add Coin"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LESS COIN MODAL */}
      {showLessModal && selectedAgency && (
        <div className="agency-recharge-modal-overlay" onClick={closeModals}>
          <div
            className="agency-recharge-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="agency-recharge-modal-header">
              <h2>Less Recharge Coin</h2>

              <button type="button" onClick={closeModals}>
                ×
              </button>
            </div>

            <div className="agency-recharge-modal-body">
              <div className="agency-recharge-info">
                <span>Agency</span>
                <strong>{selectedAgency.name}</strong>
              </div>

              <div className="agency-recharge-info">
                <span>Unique ID</span>
                <strong>{selectedAgency.uniqueId}</strong>
              </div>

              <div className="agency-recharge-info">
                <span>Current Balance</span>
                <strong>
                  {Number(
                    selectedAgency.rechargeableCoin || 0
                  ).toLocaleString()}
                </strong>
              </div>

              <label>Less Coins</label>

              <input
                type="number"
                min="1"
                step="1"
                placeholder="Enter coins"
                className="agency-recharge-modal-input"
                value={lessCoinAmount}
                onChange={(e) => setLessCoinAmount(e.target.value)}
                disabled={actionLoading}
              />
            </div>

            <div className="agency-recharge-modal-footer">
              <button
                type="button"
                className="modal-cancel-btn"
                onClick={closeModals}
              >
                Cancel
              </button>

              <button
                type="button"
                className="modal-confirm-btn less"
                onClick={handleLessCoin}
                disabled={actionLoading}
              >
                {actionLoading ? "Deducting..." : "Less Coin"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HISTORY MODAL */}
      {showHistoryModal && selectedAgency && (
        <div
          className="agency-recharge-modal-overlay"
          onClick={closeModals}
        >
          <div
            className="agency-recharge-history-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="agency-recharge-modal-header">
              <div>
                <h2>Recharge History</h2>
                <span className="agency-recharge-history-subtitle">
                  {selectedAgency.name} — {selectedAgency.uniqueId}
                </span>
              </div>

              <button type="button" onClick={closeModals}>
                ×
              </button>
            </div>

            <div className="agency-recharge-history-body">
              {historyLoading ? (
                <div className="agency-recharge-history-empty">
                  Loading History...
                </div>
              ) : history.length === 0 ? (
                <div className="agency-recharge-history-empty">
                  No Transaction History Found
                </div>
              ) : (
                <div className="agency-recharge-history-table-wrap">
                  <table className="agency-recharge-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Coins</th>
                        <th>Balance Before</th>
                        <th>Balance After</th>
                        <th>User</th>
                      </tr>
                    </thead>

                    <tbody>
                      {history.map((item) => (
                        <tr key={item._id}>
                          <td>
                            {item.date ||
                              (item.createdAt
                                ? new Date(item.createdAt).toLocaleString(
                                    "en-IN"
                                  )
                                : "-")}
                          </td>

                          <td>
                            <span
                              className={`agency-history-type ${String(
                                item.type || ""
                              ).toLowerCase()}`}
                            >
                              {item.type || "-"}
                            </span>
                          </td>

                          <td className="agency-history-coins">
                            {item.type === "LESS" ||
                            item.type === "HOST_RECHARGE"
                              ? `-${Number(item.coin || 0).toLocaleString()}`
                              : `+${Number(item.coin || 0).toLocaleString()}`}
                          </td>

                          <td>
                            {Number(
                              item.balanceBefore || 0
                            ).toLocaleString()}
                          </td>

                          <td>
                            {Number(
                              item.balanceAfter || 0
                            ).toLocaleString()}
                          </td>

                          <td>
                            {item.userName || item.user?.name || "-"}
                            {(item.userUniqueId || item.user?.uniqueId) && (
                              <>
                                {" — "}
                                {item.userUniqueId || item.user?.uniqueId}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="agency-recharge-history-footer">
              <button
                type="button"
                disabled={historyPage <= 1 || historyLoading}
                onClick={() => changeHistoryPage(historyPage - 1)}
              >
                Previous
              </button>

              <span>
                Page {historyPage} of{" "}
                {Math.max(Math.ceil(historyTotal / 10), 1)}
              </span>

              <button
                type="button"
                disabled={
                  historyPage >=
                    Math.max(Math.ceil(historyTotal / 10), 1) ||
                  historyLoading
                }
                onClick={() => changeHistoryPage(historyPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyRecharge;
